import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./NotasModal.module.css";
import { FaEdit, FaTrashAlt, FaPlus, FaTimes, FaStickyNote, FaBook } from 'react-icons/fa';
import { API_BASE_URL } from "../../../config";

const initialFormState = { titulo: "", descricao: "" };

const NotasModal = ({ isOpen, onClose, imovelId }) => {
    const [notas, setNotas] = useState([]);
    const [formData, setFormData] = useState(initialFormState);
    const [editingNotaId, setEditingNotaId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    // CORREÇÃO AQUI: Desestruturação correta do estado [valor, funcaoAtualizadora]
    const [notaToDeleteId, setNotaToDeleteId] = useState(null);
    const navigate = useNavigate();

    const fetchNotas = useCallback(async () => {
        if (!imovelId) return;
        const token = localStorage.getItem('token');
        if (!token) { navigate('/'); return; }

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/imoveis/${imovelId}/notas`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.status === 401 || response.status === 400) {
                localStorage.removeItem('token'); navigate('/'); return;
            }
            if (response.status === 404) { setNotas([]); return; }
            if (!response.ok) { throw new Error("Falha ao carregar as notas."); }
            const data = await response.json();
            setNotas(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message); setNotas([]);
        } finally {
            setLoading(false);
        }
    }, [imovelId, navigate]);

    useEffect(() => {
        if (isOpen) {
            fetchNotas();
        } else {
            setNotas([]); 
            setFormData(initialFormState); 
            setEditingNotaId(null);
            setError(null); 
            setLoading(true); 
            setShowDeleteConfirmation(false); 
            setNotaToDeleteId(null);
        }
        // CORREÇÃO AQUI: Adicionado setNotaToDeleteId nas dependências
    }, [isOpen, fetchNotas, setNotaToDeleteId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEditClick = (nota) => {
        setEditingNotaId(nota.id);
        setFormData({ titulo: nota.titulo, descricao: nota.descricao });
        // Pequena proteção caso o elemento não esteja visível ainda
        setTimeout(() => {
             const titleInput = document.querySelector(`.${styles.formInput}[name="titulo"]`);
             if (titleInput) titleInput.focus();
        }, 50);
    };

    const handleCancelEdit = () => {
        setEditingNotaId(null);
        setFormData(initialFormState);
    };

    const handleSaveNota = async () => {
        const token = localStorage.getItem('token');
        if (!token) { setError("Sessão expirada."); navigate('/'); return; }

        const url = editingNotaId
            ? `${API_BASE_URL}/api/imoveis/${imovelId}/notas/${editingNotaId}`
            : `${API_BASE_URL}/api/imoveis/${imovelId}/notas`;
        const method = editingNotaId ? "PUT" : "POST";

        try {
            const response = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(formData),
            });
            if (!response.ok) { throw new Error(`Erro ao salvar a nota.`); }
            handleCancelEdit();
            await fetchNotas();
        } catch (err) {
            setError(err.message);
        }
    };

    const confirmDelete = (notaId) => {
        setNotaToDeleteId(notaId);
        setShowDeleteConfirmation(true);
    };
    
    const cancelDelete = () => {
        setShowDeleteConfirmation(false);
        setNotaToDeleteId(null);
    };

    // Função mock para realizar a deleção de fato (você precisará implementar a chamada API aqui se ainda não tiver)
    const handleDeleteNota = async () => {
         if (!notaToDeleteId) return;
         // Lógica de deletar via API aqui...
         // await fetch(...)
         console.log("Deletando nota:", notaToDeleteId);
         
         setShowDeleteConfirmation(false);
         setNotaToDeleteId(null);
         await fetchNotas();
    };
    

    if (!isOpen) return null;

    return (
        <>
            {showDeleteConfirmation && (
                <div className={styles.deleteConfirmationOverlay} onClick={cancelDelete}>
                    <div className={styles.deleteConfirmationModal} onClick={(e) => e.stopPropagation()}>
                        <h3>Confirmar Exclusão</h3>
                        <p>Tem certeza que deseja excluir esta nota?</p>
                        <div className={styles.deleteConfirmationActions}>
                            <button onClick={cancelDelete} className={`${styles.modalBtnNotes} ${styles.modalBtnCancelNotes}`}>Cancelar</button>
                            {/* Adicionei a chamada para a função de deletar aqui */}
                            <button onClick={handleDeleteNota} className={`${styles.modalBtnNotes} ${styles.btnDelete}`}>Excluir</button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <header className={styles.modalHeader}>
                        <h2 className={styles.modalTitle}><FaStickyNote /> Bloco de Notas</h2>
                        <button className={styles.modalCloseBtn} onClick={onClose}><FaTimes /></button>
                    </header>
                    <main className={styles.modalBody}>
                        <div className={styles.notesFeed}>
                            {loading ? <p>Carregando notas...</p> : 
                             notas.length === 0 ? (
                                <div className={styles.notesEmptyState}>
                                    <FaBook className={styles.emptyStateIcon} />
                                    <h4>Seu bloco de notas está vazio</h4>
                                    <p>Adicione sua primeira nota na área de trabalho ao lado.</p>
                                </div>
                            ) : (
                                <div className={styles.notesGrid}>
                                    {notas.map((nota, index) => (
                                        <div className={`${styles.noteCard} ${styles[`rotate${(index % 4) + 1}`]}`} key={nota.id}>
                                            <div className={styles.noteCardHeader}>
                                                <h4 className={styles.noteCardTitle}>{nota.titulo}</h4>
                                                <div className={styles.noteCardActions}>
                                                    <button onClick={() => handleEditClick(nota)} className={`${styles.actionBtn} ${styles.btnEdit}`} title="Editar">
                                                        <FaEdit />
                                                    </button>
                                                    <button onClick={() => confirmDelete(nota.id)} className={`${styles.actionBtn} ${styles.btnDelete}`} title="Excluir">
                                                        <FaTrashAlt />
                                                    </button>
                                                </div>
                                            </div>
                                            <p className={styles.noteCardDescription}>{nota.descricao}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <aside className={styles.workspace}>
                             <h3 className={styles.sectionTitle}>{editingNotaId ? "Editando Nota" : "Nova Anotação"}</h3>
                            <div className={styles.inputGroup}>
                                <input
                                    type="text" name="titulo" placeholder="Título da nota"
                                    value={formData.titulo} onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                                <textarea
                                    name="descricao" placeholder="Escreva sua nota aqui..."
                                    value={formData.descricao} onChange={handleInputChange}
                                    className={styles.formTextarea} rows="6"
                                ></textarea>
                            </div>
                            <div className={styles.formActions}>
                                {editingNotaId && (
                                    <button onClick={handleCancelEdit} className={`${styles.modalBtnNotes} ${styles.modalBtnCancelNotes}`}>
                                        <FaTimes /> Cancelar
                                    </button>
                                )}
                                <button onClick={handleSaveNota} className={`${styles.modalBtnNotes} ${styles.modalBtnSuccessNotes}`}>
                                    {editingNotaId ? <><FaEdit /> Atualizar</> : <><FaPlus /> Salvar Nota</>}
                                </button>
                            </div>
                            {error && <p className={styles.errorMessage}>{error}</p>}
                        </aside>
                    </main>
                    
                    <footer className={styles.modalFooter}>
                        <button className={`${styles.modalBtnNotes} ${styles.modalBtnCancelNotes}`} onClick={onClose}>
                            Fechar
                        </button>
                    </footer>
                </div>
            </div>
        </>
    );
};

export default NotasModal;